import React, { useState, useEffect } from 'react';
import LessonLayout from '../components/LessonLayout';
import { Card, Row, Col, Alert, Button, ListGroup, Spinner, Badge } from 'react-bootstrap';

const Lesson17_APIIntegration = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    console.log('📖 Lesson 17: API Integration - Loaded');
  }, []);

  const fetchUsers = async () => {
    console.log('🌐 Fetching users from API...');
    setLoading(true);
    setError(null);

    try {
      const response = await fetch('https://jsonplaceholder.typicode.com/users');
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('✅ Users fetched successfully:', data.length, 'users');
      setUsers(data);
    } catch (err) {
      console.error('❌ Error fetching users:', err.message);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserDetails = async (userId) => {
    console.log('🌐 Fetching details for user ID:', userId);
    
    try {
      const response = await fetch(`https://jsonplaceholder.typicode.com/users/${userId}`);
      const data = await response.json();
      console.log('✅ User details fetched:', data);
      setSelectedUser(data);
    } catch (err) {
      console.error('❌ Error fetching user details:', err.message);
    }
  };

  const clearData = () => {
    console.log('🗑️ Clearing all data');
    setUsers([]);
    setSelectedUser(null);
    setError(null);
  };

  return (
    <LessonLayout 
      lessonNumber={17}
      title="API Integration" 
      description="Fetching data from APIs with proper loading and error handling"
    >
      <Alert variant="success">
        <strong>What You'll Learn:</strong>
        <ul className="mb-0 mt-2">
          <li>Fetching data with Fetch API</li>
          <li>Handling loading states</li>
          <li>Error handling</li>
          <li>Async/await in useEffect</li>
          <li>Working with REST APIs</li>
        </ul>
      </Alert>

      <Card className="mt-4 mb-3">
        <Card.Header>API Controls</Card.Header>
        <Card.Body>
          <div className="d-flex gap-2">
            <Button onClick={fetchUsers} variant="primary" disabled={loading}>
              {loading ? <><Spinner size="sm" className="me-2" />Loading...</> : 'Fetch Users'}
            </Button>
            <Button onClick={clearData} variant="secondary" disabled={loading}>
              Clear Data
            </Button>
            <Badge bg="info" className="d-flex align-items-center">
              {users.length} users loaded
            </Badge>
          </div>
        </Card.Body>
      </Card>

      {error && (
        <Alert variant="danger">
          <strong>Error:</strong> {error}
        </Alert>
      )}

      <Row>
        <Col md={6}>
          <Card className="mb-3">
            <Card.Header>Users List</Card.Header>
            <Card.Body style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {loading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" variant="primary" />
                  <p className="mt-3">Loading users...</p>
                </div>
              ) : users.length === 0 ? (
                <Alert variant="info">No users loaded. Click "Fetch Users" to load data.</Alert>
              ) : (
                <ListGroup>
                  {users.map(user => (
                    <ListGroup.Item 
                      key={user.id}
                      action
                      active={selectedUser?.id === user.id}
                      onClick={() => fetchUserDetails(user.id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <strong>{user.name}</strong>
                      <div className="text-muted small">{user.email}</div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          <Card className="mb-3">
            <Card.Header>User Details</Card.Header>
            <Card.Body>
              {selectedUser ? (
                <div>
                  <h5>{selectedUser.name}</h5>
                  <p><strong>Username:</strong> {selectedUser.username}</p>
                  <p><strong>Email:</strong> {selectedUser.email}</p>
                  <p><strong>Phone:</strong> {selectedUser.phone}</p>
                  <p><strong>Website:</strong> {selectedUser.website}</p>
                  <hr />
                  <h6>Address:</h6>
                  <p className="mb-0">
                    {selectedUser.address.street}, {selectedUser.address.suite}<br />
                    {selectedUser.address.city}, {selectedUser.address.zipcode}
                  </p>
                  <hr />
                  <h6>Company:</h6>
                  <p className="mb-0">
                    <strong>{selectedUser.company.name}</strong><br />
                    {selectedUser.company.catchPhrase}
                  </p>
                </div>
              ) : (
                <Alert variant="secondary">
                  Click on a user from the list to view details
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="mt-3">
        <Card.Header>API Integration Code Example</Card.Header>
        <Card.Body>
          <pre className="code-example">
{`// Method 1: Using async/await in useEffect
useEffect(() => {
  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await fetch('https://api.example.com/data');
      const data = await response.json();
      setData(data);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };
  
  fetchData();
}, []);

// Method 2: Using .then()
fetch('https://api.example.com/data')
  .then(response => response.json())
  .then(data => setData(data))
  .catch(error => setError(error.message));`}
          </pre>
        </Card.Body>
      </Card>

      <Alert variant="warning" className="mt-4">
        <strong>💡 Best Practices:</strong>
        <ul className="mb-0">
          <li>Always handle loading states for better UX</li>
          <li>Implement error handling for failed requests</li>
          <li>Show user-friendly error messages</li>
          <li>Use try-catch with async/await</li>
          <li>Consider cleanup in useEffect for cancelled requests</li>
          <li>Use libraries like Axios for advanced features</li>
        </ul>
      </Alert>
    </LessonLayout>
  );
};

export default Lesson17_APIIntegration;
